import React from 'react'
import { Tag, Tooltip, Button, Space, Popconfirm } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import { PROGRAM_STATUS_COLORS, isKarmaEarnNetwork } from 'const/earnPrograms'

const APPROVAL_STATUS_COLORS = {
  approved: 'green',
  pending: 'orange',
  blocked: 'red',
}

const getSharedColumns = () => [
  {
    title: 'Name',
    dataIndex: ['attributes', 'name'],
    key: 'name',
    width: 220,
    sorter: true,
    ellipsis: true,
    render: (name) => name || '-',
  },
  {
    title: 'Domain',
    dataIndex: ['attributes', 'domain'],
    key: 'domain',
    width: 180,
    ellipsis: true,
    render: (domain) => domain || '-',
  },
  {
    title: 'Network',
    dataIndex: ['attributes', 'preferred_network'],
    key: 'preferred_network',
    width: 120,
    render: (network) => network ? (
      <Tag color="blue">{network}</Tag>
    ) : '-',
  },
  {
    title: 'Program Status',
    dataIndex: ['attributes', 'program_status'],
    key: 'program_status',
    width: 140,
    render: (status) => status ? (
      <Tag color={PROGRAM_STATUS_COLORS[status] || 'default'}>
        {status.replace(/_/g, ' ')}
      </Tag>
    ) : '-',
  },
  {
    title: 'Platform',
    dataIndex: ['attributes', 'website_id'],
    key: 'website_id',
    width: 120,
    render: (websiteId) => websiteId || '-',
  },
  {
    title: 'Program ID',
    dataIndex: ['attributes', 'program_id'],
    key: 'program_id',
    width: 130,
    render: (id) => id || '-',
  },
  {
    title: 'Created At',
    dataIndex: ['attributes', 'created_at'],
    key: 'created_at',
    width: 150,
    sorter: true,
    render: (date) => date ? moment(date).format('YYYY-MM-DD HH:mm') : '-',
  },
]

const getActionsColumn = ({ onEdit, onDelete }) => ({
  title: 'Actions',
  key: 'actions',
  width: 110,
  fixed: 'right',
  render: (_, record) => {
    if (!isKarmaEarnNetwork(record.attributes?.preferred_network)) return null
    return (
      <Space>
        <Tooltip title="Edit">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Tooltip>
        <Popconfirm
          title="Delete this program?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(record)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )
  },
})

export const getGameColumns = (handlers) => [
  ...getSharedColumns(),
  {
    title: 'Goals',
    key: 'goals_count',
    width: 80,
    render: (_, record) => {
      const count = record.goals?.length || 0
      return count > 0 ? <Tag>{count}</Tag> : '-'
    },
  },
  getActionsColumn(handlers),
]

export const getOfferColumns = (handlers) => [
  ...getSharedColumns(),
  {
    title: 'Approval Status',
    dataIndex: 'approval_status',
    key: 'approval_status',
    width: 140,
    render: (status) => status ? (
      <Tag color={APPROVAL_STATUS_COLORS[status] || 'default'}>
        {status}
      </Tag>
    ) : '-',
  },
  {
    title: 'Reviewed By',
    key: 'reviewed_by',
    width: 130,
    render: (_, record) => {
      const userId = record.approval?.attributes?.reviewed_by_user_id
      return userId || '\u2014'
    },
  },
  {
    title: 'Reviewed At',
    key: 'reviewed_at',
    width: 150,
    render: (_, record) => {
      const date = record.approval?.attributes?.reviewed_at
      return date ? moment(date).format('YYYY-MM-DD HH:mm') : '\u2014'
    },
  },
  getActionsColumn(handlers),
]

export const goalColumns = [
  {
    title: 'Goal ID',
    dataIndex: ['attributes', 'goal_id'],
    key: 'goal_id',
    width: 120,
  },
  {
    title: 'Name',
    dataIndex: ['attributes', 'name'],
    key: 'name',
    width: 200,
  },
  {
    title: 'Type',
    dataIndex: ['attributes', 'type'],
    key: 'type',
    width: 120,
    render: (type) => type ? (
      <Tag>{type.replace(/_/g, ' ')}</Tag>
    ) : '-',
  },
  {
    title: 'Position',
    dataIndex: ['attributes', 'position'],
    key: 'position',
    width: 80,
  },
  {
    title: 'Linear',
    dataIndex: ['attributes', 'linear'],
    key: 'linear',
    width: 80,
    render: (linear) => linear
      ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
      : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
  },
  {
    title: 'Compliance Metric',
    dataIndex: ['attributes', 'compliance_metric'],
    key: 'compliance_metric',
    width: 150,
    render: (metric) => metric ? metric.replace(/_/g, ' ') : '-',
  },
  {
    title: 'Compliance Target',
    dataIndex: ['attributes', 'compliance_target'],
    key: 'compliance_target',
    width: 130,
    render: (target) => target != null ? target : '-',
  },
  {
    title: 'Status',
    dataIndex: ['attributes', 'status'],
    key: 'status',
    width: 100,
    render: (status) => status ? (
      <Tag color={status === 'active' ? 'green' : 'default'}>
        {status}
      </Tag>
    ) : '-',
  },
]
